import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Edit2, 
  Trash2,
  Clock,
  Moon,
  Calendar as CalendarIcon,
  Layers,
  Users,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  Palmtree,
  Coffee
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shifts = () => {
  const { isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [sequenceAssignments, setSequenceAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  
  // Template form
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSplitShift, setIsSplitShift] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    start_time: '09:00',
    end_time: '17:00',
    break_start: '13:00',
    break_end: '14:00',
    color: '#4F46E5',
    is_night_shift: false,
    start_time_2: '',
    end_time_2: ''
  });

  // Assignment form
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    employee_id: '',
    shift_template_id: '',
    date: ''
  });

  // Sequence form
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [sequenceForm, setSequenceForm] = useState({
    name: '',
    pattern: [],
    weeks: 1
  });

  // Sequence Assignment form
  const [seqAssignDialogOpen, setSeqAssignDialogOpen] = useState(false);
  const [seqAssignForm, setSeqAssignForm] = useState({
    employee_id: '',
    sequence_id: '',
    start_date: '',
    end_date: ''
  });

  // Calendar view for individual shifts
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [employeeVacations, setEmployeeVacations] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, assignmentsRes, sequencesRes, employeesRes, seqAssignRes] = await Promise.all([
        axios.get(`${API_URL}/shifts/templates`),
        axios.get(`${API_URL}/shifts/assignments`),
        axios.get(`${API_URL}/shifts/sequences`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/shifts/sequence-assignments`)
      ]);
      setTemplates(templatesRes.data);
      setAssignments(assignmentsRes.data);
      setSequences(sequencesRes.data);
      setEmployees(employeesRes.data.filter(e => e.is_active !== false));
      setSequenceAssignments(seqAssignRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee vacations when employee is selected
  const fetchEmployeeVacations = async (employeeId) => {
    if (!employeeId) {
      setEmployeeVacations([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/vacations?employee_id=${employeeId}&status=approved`);
      setEmployeeVacations(res.data);
    } catch (error) {
      console.error('Error fetching vacations:', error);
      setEmployeeVacations([]);
    }
  };

  // Fetch assignments for specific employee (more efficient for calendar view)
  const fetchEmployeeAssignments = async (employeeId) => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${API_URL}/shifts/assignments?employee_id=${employeeId}`);
      // Merge with existing assignments, replacing any for this employee
      setAssignments(prev => {
        const otherAssignments = prev.filter(a => a.employee_id !== employeeId);
        return [...otherAssignments, ...res.data];
      });
    } catch (error) {
      console.error('Error fetching employee assignments:', error);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before the first day of month
    const startDayOfWeek = firstDay.getDay();
    const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday = 0
    for (let i = 0; i < adjustedStart; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  };

  const formatDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getAssignmentForDate = (date, empId) => {
    if (!date || !empId) return null;
    const dateStr = formatDateString(date);
    return assignments.find(a => a.employee_id === empId && a.date === dateStr);
  };

  const isVacationDay = (date, empId) => {
    if (!date || !empId) return null;
    const dateStr = formatDateString(date);
    return employeeVacations.find(v => 
      v.status === 'approved' && dateStr >= v.start_date && dateStr <= v.end_date
    );
  };

  const handleCalendarDayClick = async (date) => {
    if (!date) return;
    
    if (!selectedEmployee) {
      alert(language === 'es' ? 'Selecciona un empleado primero' : 'Select an employee first');
      return;
    }
    
    if (!selectedTemplate) {
      alert(language === 'es' ? 'Selecciona un turno para asignar' : 'Select a shift to assign');
      return;
    }
    
    const dateStr = formatDateString(date);
    const existingAssignment = getAssignmentForDate(date, selectedEmployee);
    
    if (existingAssignment && existingAssignment.shift_template_id === selectedTemplate) {
      // If clicking same shift, delete it
      try {
        await axios.delete(`${API_URL}/shifts/assignments/${existingAssignment.id}`);
        await fetchEmployeeAssignments(selectedEmployee);
      } catch (error) {
        alert(error.response?.data?.detail || t('error_occurred'));
      }
    } else {
      // Assign new shift (will replace existing if any)
      try {
        await axios.post(`${API_URL}/shifts/assignments`, {
          employee_id: selectedEmployee,
          shift_template_id: selectedTemplate,
          date: dateStr
        });
        await fetchEmployeeAssignments(selectedEmployee);
      } catch (error) {
        console.error('Error assigning shift:', error);
        alert(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  const handleRemoveShift = async (date) => {
    if (!date || !selectedEmployee) return;
    const existingAssignment = getAssignmentForDate(date, selectedEmployee);
    if (existingAssignment) {
      try {
        await axios.delete(`${API_URL}/shifts/assignments/${existingAssignment.id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  // Template handlers
  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...templateForm };
      // Only include split shift times if enabled
      if (!isSplitShift) {
        payload.start_time_2 = null;
        payload.end_time_2 = null;
      }
      
      if (editingTemplate) {
        await axios.put(`${API_URL}/shifts/templates/${editingTemplate.id}`, payload);
      } else {
        await axios.post(`${API_URL}/shifts/templates`, payload);
      }
      fetchData();
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      resetTemplateForm();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    const hasSplitShift = !!(template.start_time_2 && template.end_time_2);
    setIsSplitShift(hasSplitShift);
    setTemplateForm({
      name: template.name,
      start_time: template.start_time,
      end_time: template.end_time,
      break_start: template.break_start || '13:00',
      break_end: template.break_end || '14:00',
      color: template.color || '#4F46E5',
      is_night_shift: template.is_night_shift || false,
      start_time_2: template.start_time_2 || '',
      end_time_2: template.end_time_2 || ''
    });
    setTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta plantilla de turno?' : 'Delete this shift template?')) return;
    try {
      await axios.delete(`${API_URL}/shifts/templates/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const resetTemplateForm = () => {
    setIsSplitShift(false);
    setTemplateForm({
      name: '',
      start_time: '09:00',
      end_time: '17:00',
      break_start: '13:00',
      break_end: '14:00',
      color: '#4F46E5',
      is_night_shift: false,
      start_time_2: '',
      end_time_2: ''
    });
  };

  // Assignment handlers
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/shifts/assignments`, assignForm);
      fetchData();
      setAssignDialogOpen(false);
      setAssignForm({ employee_id: '', shift_template_id: '', date: '' });
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta asignación?' : 'Delete this assignment?')) return;
    try {
      await axios.delete(`${API_URL}/shifts/assignments/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  // Sequence handlers
  const handleSequenceSubmit = async (e) => {
    e.preventDefault();
    try {
      const validPattern = sequenceForm.pattern.filter(p => p && p.trim() !== '');
      if (validPattern.length === 0) {
        alert(language === 'es' ? 'Debe seleccionar al menos un turno en el patrón' : 'You must select at least one shift in the pattern');
        return;
      }
      
      const payload = {
        ...sequenceForm,
        pattern: validPattern
      };

      if (editingSequence) {
        await axios.put(`${API_URL}/shifts/sequences/${editingSequence.id}`, payload);
      } else {
        await axios.post(`${API_URL}/shifts/sequences`, payload);
      }
      
      fetchData();
      setSequenceDialogOpen(false);
      setEditingSequence(null);
      setSequenceForm({ name: '', pattern: [], weeks: 1 });
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      alert(typeof errorMsg === 'string' ? errorMsg : t('error_occurred'));
    }
  };

  const handleEditSequence = (sequence) => {
    setEditingSequence(sequence);
    setSequenceForm({
      name: sequence.name,
      pattern: sequence.pattern || [],
      weeks: sequence.weeks || 1
    });
    setSequenceDialogOpen(true);
  };

  const handleDeleteSequence = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta secuencia? También se eliminarán todas las asignaciones asociadas.' : 'Delete this sequence? All associated assignments will also be deleted.')) return;
    try {
      await axios.delete(`${API_URL}/shifts/sequences/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  // Sequence Assignment handlers
  const handleSeqAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/shifts/sequence-assignments`, null, {
        params: seqAssignForm
      });
      fetchData();
      setSeqAssignDialogOpen(false);
      setSeqAssignForm({ employee_id: '', sequence_id: '', start_date: '', end_date: '' });
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleDeleteSeqAssignment = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta asignación de secuencia? También se eliminarán los turnos generados.' : 'Delete this sequence assignment? Generated shifts will also be deleted.')) return;
    try {
      await axios.delete(`${API_URL}/shifts/sequence-assignments/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const getEmployeeName = (empId) => employees.find(e => e.id === empId)?.name || 'N/A';
  const getTemplateName = (templateId) => templates.find(t => t.id === templateId)?.name || 'N/A';
  const getSequenceName = (seqId) => sequences.find(s => s.id === seqId)?.name || 'N/A';
  const getTemplateColor = (templateId) => templates.find(t => t.id === templateId)?.color || '#94a3b8';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('shifts')}</h1>
        <p className="text-slate-500">
          {language === 'es' ? 'Gestión de turnos, plantillas y secuencias de rotación' : 'Manage shifts, templates and rotation sequences'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Clock className="w-4 h-4 mr-2" />
            {t('templates')}
          </TabsTrigger>
          <TabsTrigger value="sequences" data-testid="tab-sequences">
            <Layers className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Secuencias' : 'Sequences'}
          </TabsTrigger>
          <TabsTrigger value="sequence-assignments" data-testid="tab-seq-assignments">
            <Users className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Asignaciones' : 'Assignments'}
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">
            <CalendarIcon className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Turnos Individuales' : 'Individual Shifts'}
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={templateDialogOpen} onOpenChange={(open) => {
              setTemplateDialogOpen(open);
              if (!open) {
                setEditingTemplate(null);
                resetTemplateForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Crear Plantilla' : 'Create Template'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">
                    {editingTemplate 
                      ? (language === 'es' ? 'Editar Plantilla de Turno' : 'Edit Shift Template')
                      : (language === 'es' ? 'Nueva Plantilla de Turno' : 'New Shift Template')
                    }
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTemplateSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('name')}</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Hora inicio' : 'Start time'}</Label>
                      <Input
                        type="time"
                        value={templateForm.start_time}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, start_time: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Hora fin' : 'End time'}</Label>
                      <Input
                        type="time"
                        value={templateForm.end_time}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, end_time: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Inicio descanso' : 'Break start'}</Label>
                      <Input
                        type="time"
                        value={templateForm.break_start}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, break_start: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Fin descanso' : 'Break end'}</Label>
                      <Input
                        type="time"
                        value={templateForm.break_end}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, break_end: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  {/* Split Shift Toggle */}
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Switch
                      checked={isSplitShift}
                      onCheckedChange={setIsSplitShift}
                      data-testid="split-shift-toggle"
                    />
                    <Label className="flex items-center gap-2 text-amber-800">
                      <Coffee className="w-4 h-4" />
                      {language === 'es' ? 'Turno partido (jornada con 2 periodos)' : 'Split shift (2 work periods)'}
                    </Label>
                  </div>
                  
                  {/* Second Period - Only shown when split shift is enabled */}
                  {isSplitShift && (
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <Label className="text-sm font-medium text-slate-700">
                        {language === 'es' ? '2º Periodo de trabajo' : '2nd Work Period'}
                      </Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'es' ? 'Entrada 2' : 'Start time 2'}</Label>
                          <Input
                            type="time"
                            value={templateForm.start_time_2}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, start_time_2: e.target.value }))}
                            required={isSplitShift}
                            data-testid="start-time-2-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'es' ? 'Salida 2' : 'End time 2'}</Label>
                          <Input
                            type="time"
                            value={templateForm.end_time_2}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, end_time_2: e.target.value }))}
                            required={isSplitShift}
                            data-testid="end-time-2-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('color')}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          value={templateForm.color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={templateForm.color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={templateForm.is_night_shift}
                        onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, is_night_shift: checked }))}
                      />
                      <Label className="flex items-center gap-2">
                        <Moon className="w-4 h-4" />
                        {language === 'es' ? 'Turno nocturno' : 'Night shift'}
                      </Label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {editingTemplate ? t('update') : t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {templates.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {language === 'es' ? 'No hay plantillas de turno. Crea una para empezar.' : 'No shift templates. Create one to start.'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead>{language === 'es' ? 'Horario' : 'Schedule'}</TableHead>
                      <TableHead>{language === 'es' ? 'Descanso' : 'Break'}</TableHead>
                      <TableHead>{t('type')}</TableHead>
                      <TableHead className="text-right">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map(template => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: template.color }}
                            />
                            <span className="font-medium">{template.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div>{template.start_time} - {template.end_time}</div>
                            {template.start_time_2 && template.end_time_2 && (
                              <div className="text-xs text-amber-600 flex items-center gap-1">
                                <Coffee className="w-3 h-3" />
                                {template.start_time_2} - {template.end_time_2}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{template.break_start} - {template.break_end}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {template.is_night_shift && (
                              <Badge variant="secondary" className="bg-slate-800 text-white">
                                <Moon className="w-3 h-3 mr-1" />
                                {language === 'es' ? 'Nocturno' : 'Night'}
                              </Badge>
                            )}
                            {template.start_time_2 && template.end_time_2 && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                                <Coffee className="w-3 h-3 mr-1" />
                                {language === 'es' ? 'Partido' : 'Split'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleEditTemplate(template)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteTemplate(template.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sequences Tab */}
        <TabsContent value="sequences" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={sequenceDialogOpen} onOpenChange={(open) => {
              setSequenceDialogOpen(open);
              if (!open) {
                setEditingSequence(null);
                setSequenceForm({ name: '', pattern: [], weeks: 1 });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Crear Secuencia' : 'Create Sequence'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">
                    {editingSequence
                      ? (language === 'es' ? 'Editar Secuencia de Turnos' : 'Edit Shift Sequence')
                      : (language === 'es' ? 'Nueva Secuencia de Turnos' : 'New Shift Sequence')
                    }
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSequenceSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('name')}</Label>
                      <Input
                        value={sequenceForm.name}
                        onChange={(e) => setSequenceForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                        placeholder={language === 'es' ? 'Ej: Rotación Mañana/Tarde' : 'E.g.: Morning/Afternoon Rotation'}
                        data-testid="sequence-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Número de semanas' : 'Number of weeks'}</Label>
                      <Input
                        type="number"
                        min="1"
                        max="8"
                        value={sequenceForm.weeks}
                        onChange={(e) => {
                          const weeks = parseInt(e.target.value) || 1;
                          setSequenceForm(prev => ({ 
                            ...prev, 
                            weeks,
                            pattern: Array(weeks * 7).fill('')
                          }));
                        }}
                        data-testid="sequence-weeks-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label>{language === 'es' ? 'Patrón de turnos' : 'Shift pattern'}</Label>
                    <p className="text-sm text-slate-500">
                      {language === 'es' 
                        ? 'Selecciona el turno para cada día de la secuencia. "Libre" = día de descanso.'
                        : 'Select the shift for each day of the sequence. "Off" = day off.'}
                    </p>
                    
                    {Array.from({ length: sequenceForm.weeks || 1 }, (_, weekIndex) => (
                      <div key={weekIndex} className="border rounded-lg p-3 bg-slate-50">
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          {language === 'es' ? `Semana ${weekIndex + 1}` : `Week ${weekIndex + 1}`}
                        </p>
                        <div className="grid grid-cols-7 gap-2">
                          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, dayIndex) => {
                            const patternIndex = weekIndex * 7 + dayIndex;
                            return (
                              <div key={dayIndex} className="space-y-1">
                                <Label className="text-xs text-center block text-slate-500">{day}</Label>
                                <Select
                                  value={sequenceForm.pattern[patternIndex] || 'off'}
                                  onValueChange={(value) => {
                                    const newPattern = [...sequenceForm.pattern];
                                    newPattern[patternIndex] = value;
                                    setSequenceForm(prev => ({ ...prev, pattern: newPattern }));
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="off">
                                      <span className="text-slate-400">{language === 'es' ? 'Libre' : 'Off'}</span>
                                    </SelectItem>
                                    {templates.map(template => (
                                      <SelectItem key={template.id} value={template.id}>
                                        <div className="flex items-center gap-2">
                                          <div 
                                            className="w-2 h-2 rounded-full" 
                                            style={{ backgroundColor: template.color }}
                                          />
                                          <span className="truncate">{template.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setSequenceDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {editingSequence ? t('update') : t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {sequences.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {t('no_results')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead>{language === 'es' ? 'Duración' : 'Duration'}</TableHead>
                      <TableHead>{language === 'es' ? 'Patrón' : 'Pattern'}</TableHead>
                      <TableHead className="text-right">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sequences.map(sequence => (
                      <TableRow key={sequence.id}>
                        <TableCell className="font-medium">{sequence.name}</TableCell>
                        <TableCell>
                          {sequence.weeks} {language === 'es' ? 'semana(s)' : 'week(s)'} - {(sequence.pattern?.length || 0)} {language === 'es' ? 'días' : 'days'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {sequence.pattern?.slice(0, 14).map((templateId, i) => (
                              <div 
                                key={i}
                                className="w-6 h-6 rounded text-xs flex items-center justify-center text-white"
                                style={{ backgroundColor: templateId === 'off' ? '#94a3b8' : getTemplateColor(templateId) }}
                                title={templateId === 'off' ? (language === 'es' ? 'Libre' : 'Off') : getTemplateName(templateId)}
                              >
                                {templateId === 'off' ? '-' : (i % 7 + 1)}
                              </div>
                            ))}
                            {(sequence.pattern?.length || 0) > 14 && (
                              <span className="text-xs text-slate-500">+{sequence.pattern.length - 14}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleEditSequence(sequence)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteSequence(sequence.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sequence Assignments Tab */}
        <TabsContent value="sequence-assignments" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={seqAssignDialogOpen} onOpenChange={setSeqAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <PlayCircle className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Asignar Secuencia' : 'Assign Sequence'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">
                    {language === 'es' ? 'Asignar Secuencia a Empleado' : 'Assign Sequence to Employee'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSeqAssignSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('employee')}</Label>
                    <Select
                      value={seqAssignForm.employee_id}
                      onValueChange={(value) => setSeqAssignForm(prev => ({ ...prev, employee_id: value }))}
                    >
                      <SelectTrigger data-testid="seq-assign-employee">
                        <SelectValue placeholder={language === 'es' ? 'Seleccionar empleado' : 'Select employee'} />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Secuencia' : 'Sequence'}</Label>
                    <Select
                      value={seqAssignForm.sequence_id}
                      onValueChange={(value) => setSeqAssignForm(prev => ({ ...prev, sequence_id: value }))}
                    >
                      <SelectTrigger data-testid="seq-assign-sequence">
                        <SelectValue placeholder={language === 'es' ? 'Seleccionar secuencia' : 'Select sequence'} />
                      </SelectTrigger>
                      <SelectContent>
                        {sequences.map(seq => (
                          <SelectItem key={seq.id} value={seq.id}>
                            {seq.name} ({seq.weeks} {language === 'es' ? 'sem.' : 'wk.'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('start_date')}</Label>
                      <Input
                        type="date"
                        value={seqAssignForm.start_date}
                        onChange={(e) => setSeqAssignForm(prev => ({ ...prev, start_date: e.target.value }))}
                        required
                        data-testid="seq-assign-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('end_date')}</Label>
                      <Input
                        type="date"
                        value={seqAssignForm.end_date}
                        onChange={(e) => setSeqAssignForm(prev => ({ ...prev, end_date: e.target.value }))}
                        required
                        data-testid="seq-assign-end"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === 'es' 
                      ? 'Al asignar una secuencia se generarán automáticamente los turnos individuales para cada día del período.'
                      : 'Assigning a sequence will automatically generate individual shifts for each day of the period.'}
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setSeqAssignDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {language === 'es' ? 'Asignar' : 'Assign'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {sequenceAssignments.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {language === 'es' 
                    ? 'No hay asignaciones de secuencias. Asigna una secuencia a un empleado para empezar.'
                    : 'No sequence assignments. Assign a sequence to an employee to start.'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('employee')}</TableHead>
                      <TableHead>{language === 'es' ? 'Secuencia' : 'Sequence'}</TableHead>
                      <TableHead>{t('start_date')}</TableHead>
                      <TableHead>{t('end_date')}</TableHead>
                      <TableHead className="text-right">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sequenceAssignments.map(assignment => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium">{getEmployeeName(assignment.employee_id)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-indigo-50">
                            {getSequenceName(assignment.sequence_id)}
                          </Badge>
                        </TableCell>
                        <TableCell>{assignment.start_date}</TableCell>
                        <TableCell>{assignment.end_date}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteSeqAssignment(assignment.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Individual Assignments Tab - Calendar View */}
        <TabsContent value="assignments" className="space-y-4">
          {/* Employee and Template Selection */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('employee')}</Label>
                  <Select
                    value={selectedEmployee}
                    onValueChange={(value) => {
                      setSelectedEmployee(value);
                      fetchEmployeeVacations(value);
                      fetchEmployeeAssignments(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar empleado' : 'Select employee'} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Turno a asignar (click en día)' : 'Shift to assign (click on day)'}</Label>
                  <Select
                    value={selectedTemplate}
                    onValueChange={setSelectedTemplate}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar turno' : 'Select shift'} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: template.color }}
                            />
                            {template.name} ({template.start_time} - {template.end_time})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedEmployee && selectedTemplate && (
                <p className="text-sm text-slate-500 mt-2">
                  {language === 'es' 
                    ? 'Haz click en un día para asignar el turno. Click en un turno ya asignado para eliminarlo.' 
                    : 'Click on a day to assign the shift. Click on an assigned shift to remove it.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Calendar */}
          {selectedEmployee ? (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <CardTitle className="font-['Manrope'] text-lg">
                    {calendarDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {(language === 'es' ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map(day => (
                    <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">{day}</div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(calendarDate).map((date, index) => {
                    const assignment = getAssignmentForDate(date, selectedEmployee);
                    const template = assignment ? templates.find(t => t.id === assignment.shift_template_id) : null;
                    const vacation = isVacationDay(date, selectedEmployee);
                    const today = isToday(date);

                    return (
                      <div
                        key={index}
                        className={`min-h-[80px] p-1 rounded-lg border transition-all ${
                          !date ? 'bg-slate-50 border-transparent' :
                          today ? 'border-indigo-500 bg-indigo-50 cursor-pointer' :
                          vacation ? 'bg-green-50 border-green-200' :
                          selectedTemplate ? 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer' :
                          'border-slate-200'
                        }`}
                        onClick={() => date && !vacation && handleCalendarDayClick(date)}
                      >
                        {date && (
                          <>
                            <div className={`text-xs font-medium mb-1 ${today ? 'text-indigo-600' : 'text-slate-700'}`}>
                              {date.getDate()}
                            </div>
                            {vacation ? (
                              <div className="bg-green-500 rounded px-1 py-0.5 text-xs text-white">
                                <div className="font-medium truncate flex items-center gap-1">
                                  <Palmtree className="w-3 h-3" />
                                  {language === 'es' ? 'Vacaciones' : 'Vacation'}
                                </div>
                              </div>
                            ) : template ? (
                              <div 
                                className="rounded px-1 py-0.5 text-xs text-white cursor-pointer group relative"
                                style={{ backgroundColor: template.color || '#6366f1' }}
                                title={`${template.name}\n${template.start_time} - ${template.end_time}${template.break_start ? `\n${language === 'es' ? 'Descanso' : 'Break'}: ${template.break_start} - ${template.break_end}` : ''}`}
                              >
                                <div className="font-medium truncate">{template.name}</div>
                                <div className="opacity-80 text-[10px]">{template.start_time} - {template.end_time}</div>
                                {/* Delete button on hover */}
                                <button
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] hidden group-hover:flex items-center justify-center"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveShift(date);
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ) : selectedTemplate ? (
                              <div 
                                className="text-xs italic h-8 flex items-center justify-center rounded text-indigo-400 bg-indigo-50 border border-dashed border-indigo-300 cursor-pointer hover:bg-indigo-100 transition-colors"
                                data-testid={`add-shift-${formatDateString(date)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCalendarDayClick(date);
                                }}
                              >
                                {language === 'es' ? '+ Añadir' : '+ Add'}
                              </div>
                            ) : (
                              <div className="text-xs italic h-8 flex items-center justify-center rounded text-slate-300">
                                {language === 'es' ? 'Libre' : 'Off'}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span>{language === 'es' ? 'Vacaciones' : 'Vacation'}</span>
                  </div>
                  {templates.slice(0, 5).map(template => (
                    <div key={template.id} className="flex items-center gap-2 text-xs text-slate-600">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: template.color }} />
                      <span>{template.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center text-slate-500">
                {language === 'es' ? 'Selecciona un empleado para ver y editar su calendario de turnos' : 'Select an employee to view and edit their shift calendar'}
              </CardContent>
            </Card>
          )}

          {/* Quick assignment list */}
          {selectedEmployee && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-base">
                  {language === 'es' ? 'Turnos asignados este mes' : 'Assigned shifts this month'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {assignments.filter(a => {
                  const monthStart = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}-01`;
                  const monthEnd = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}-31`;
                  return a.employee_id === selectedEmployee && a.date >= monthStart && a.date <= monthEnd;
                }).length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {language === 'es' ? 'No hay turnos asignados este mes' : 'No shifts assigned this month'}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('date')}</TableHead>
                        <TableHead>{language === 'es' ? 'Turno' : 'Shift'}</TableHead>
                        <TableHead>{language === 'es' ? 'Horario' : 'Schedule'}</TableHead>
                        <TableHead className="text-right">{t('actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments
                        .filter(a => {
                          const monthStart = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}-01`;
                          const monthEnd = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}-31`;
                          return a.employee_id === selectedEmployee && a.date >= monthStart && a.date <= monthEnd;
                        })
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map(assignment => {
                          const template = templates.find(t => t.id === assignment.shift_template_id);
                          return (
                            <TableRow key={assignment.id}>
                              <TableCell className="font-medium">{assignment.date}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: template?.color || '#6366f1' }}
                                  />
                                  {template?.name || 'Unknown'}
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-600">
                                {template ? `${template.start_time} - ${template.end_time}` : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteAssignment(assignment.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Shifts;
